import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalculateIcon from "@mui/icons-material/Calculate";
import RefreshIcon from "@mui/icons-material/Refresh";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_TopToolbar,
  MRT_ToggleGlobalFilterButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
  type MRT_ColumnDef,
  type MRT_TableInstance,
} from "material-react-table";
import Accordion from "react-bootstrap/Accordion";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { Row, Container } from "reactstrap";
import { TableColumnVisibilityMenu } from "../../Components/SharedComponents/TableColumnVisibilityMenu";

import {
  JobKey,
  JobPatch,
  JobsState,
  RunParams,
} from "./Initialmarginsummary.types";
import {
  BUSINESS_DATE_FORMAT,
  EMPTY_JOBS,
  IM_RESULT_HEADERS,
  JOB_SECTIONS,
  NON_MONEY_KEYS,
  STATUS_CHIP,
  TILL_DATE_FORMAT,
  buildRunParams,
  buildStartedJobs,
  formatMoney,
  getRunErrorMessage,
  moneyColor,
  pollJob,
  startRunAll,
} from "./Initialmarginsummary.actions";
import {
  MARGIN_BY_MODES,
  ROW_TYPE_KEY,
  buildCombinedRows,
  combinedRowSx,
  formatBusinessDate,
  marginByLabel,
} from "./initialMargin.actions";
import type { MarginBy } from "./initialMargin.types";

const TABLE_MAX_HEIGHT = "400px";
const PAGE_SIZE = 100;
const BORDER = "#e6eaf0";
const BRAND = "#009db5";
// rows after which the vertical scrollbar appears (maxHeight 400px, compact rows)
const SCROLLBAR_ROWS = 12;
const SCROLLBAR_WIDTH = 15;
// the only columns that get a total in the totals bar under the toolbar
const TOTAL_KEYS = ["irm_sum", "buffered_im"];
const TOTAL_KEY_SET: Set<string> = new Set(TOTAL_KEYS);

const datePickerSx = {
  flex: "0 1 170px",
  minWidth: 150,
  maxWidth: 180,
  "& .MuiInputBase-root": {
    fontSize: "0.75rem",
    height: 34,
    background: "linear-gradient(160deg, #edf4ff 0%, #f5f8ff 100%)",
    borderRadius: "8px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#3b4a66",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(150,160,180,0.4)",
  },
  "& input": { fontFamily: "monospace", padding: "6px 8px" },
};

// start/end (ms) of one job's calculation; end is null while it is still running
type JobTiming = { start: number; end: number | null };

// last business day: Mon -> Fri, Sun -> Fri, Sat -> Fri, otherwise yesterday
// (weekends only, no holiday calendar)
export const getLastBusinessDay = () => {
  const d = dayjs();
  const back = d.day() === 0 ? 2 : d.day() === 1 ? 3 : 1;
  return d.subtract(back, "day");
};

// 65000 -> "1m 05s", 3725000 -> "1h 02m 05s"
const formatDuration = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${total}s`;
};

// Counts up while the job runs, then shows how long it took.
// Ticks on its own so the result tables are not re-rendered every second.
const ElapsedTimer: React.FC<{ timing: JobTiming }> = ({ timing }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (timing.end !== null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timing.end]);

  const running = timing.end === null;
  const elapsed = (running ? now : (timing.end as number)) - timing.start;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        fontSize: "0.7rem",
        color: running ? BRAND : "#64748b",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <TimerOutlinedIcon sx={{ fontSize: 14 }} />
      {running ? formatDuration(elapsed) : `Took ${formatDuration(elapsed)}`}
    </Box>
  );
};

// IRM (shown as "Total IRM") = IRM1 + IRM2, rounded to 2 decimals to avoid float noise
const withIrmSum = (rows: any[]) =>
  rows.map((r) => {
    const a = typeof r?.im1 === "number" ? r.im1 : null;
    const b = typeof r?.im2 === "number" ? r.im2 : null;
    return {
      ...r,
      irm_sum:
        a === null && b === null
          ? null
          : Math.round(((a ?? 0) + (b ?? 0)) * 100) / 100,
    };
  });

/* ---------- table pieces (same design as P&L Summary View-3) ---------- */

// Totals row under the toolbar; follows the table's horizontal scroll
const TotalsBar = <TData extends Record<string, any>,>({
  table,
  totals,
  innerRef,
}: {
  table: MRT_TableInstance<TData>;
  totals: Record<string, number>;
  innerRef: React.RefObject<HTMLDivElement>;
}) => {
  const leafColumns = table.getVisibleLeafColumns();
  // bar ends exactly where the last column ends
  const columnsWidth = leafColumns.reduce((sum, c) => sum + c.getSize(), 0);

  return (
    <Box
      sx={{
        overflow: "hidden",
        boxSizing: "border-box",
        width: columnsWidth + 2,
        maxWidth: "100%",
        backgroundColor: "#fff",
        border: "1px solid #e0e0e0",
        borderBottom: "1px solid #b0bec5",
      }}
    >
      <Box
        ref={innerRef}
        sx={{ display: "flex", width: "max-content", willChange: "transform" }}
      >
        {leafColumns.map((column, idx) => {
          const size = column.getSize();
          const id = column.id;
          const isLast = idx === leafColumns.length - 1;
          const isLabel = id === "portfolio";
          const isTotal = TOTAL_KEY_SET.has(id);
          const value = totals[id] ?? 0;

          return (
            <Box
              key={id}
              sx={{
                width: size,
                minWidth: size,
                maxWidth: size,
                flexShrink: 0,
                padding: "4px 8px",
                // no border on the last cell: clean cut at the column edge
                borderRight: isLast ? "none" : "1px solid #dcdcdc",
                display: "flex",
                alignItems: "center",
              }}
            >
              {isLabel && (
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: "0.78rem",
                    color: "#2c3e50",
                    letterSpacing: "0.02em",
                  }}
                >
                  TOTAL
                </span>
              )}
              {isTotal && (
                <Box
                  component="span"
                  sx={{
                    color: value < 0 ? "#dc2626" : "#1b5e20",
                    padding: "3px 12px",
                    borderRadius: "4px",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    display: "inline-block",
                  }}
                >
                  {formatMoney(value)}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

type ResultTableProps = {
  columns: MRT_ColumnDef<any>[];
  data: any[];
  totals: Record<string, number> | null;
  // HOF + Strategy: rows must stay grouped per book, so no sorting / filtering
  lockOrder: boolean;
  needsPaging: boolean;
};

// One table per section. Its own component so each has its own table instance.
const ResultTable: React.FC<ResultTableProps> = ({
  columns,
  data,
  totals,
  lockOrder,
  needsPaging,
}) => {
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const totalsInnerRef = useRef<HTMLDivElement | null>(null);

  // exact width of all columns (+2 for the container border, + scrollbar when rows scroll)
  const tableWidth = useMemo(
    () =>
      columns.reduce((sum, c) => sum + (c.size ?? 150), 0) +
      2 +
      (data.length > SCROLLBAR_ROWS ? SCROLLBAR_WIDTH : 0),
    [columns, data.length]
  );

  // keep the totals bar aligned after the data changes
  useEffect(() => {
    if (tableContainerRef.current && totalsInnerRef.current) {
      totalsInnerRef.current.style.transform = `translateX(-${tableContainerRef.current.scrollLeft}px)`;
    }
  }, [data]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnFilters: !lockOrder,
    enableSorting: !lockOrder,
    enablePagination: needsPaging,
    enableBottomToolbar: needsPaging,
    enablePinning: false,
    enableColumnPinning: false,
    enableColumnResizing: true,
    enableColumnOrdering: false,
    enableColumnActions: false,
    enableStickyHeader: true,
    enableRowSelection: false,
    enableRowActions: false,
    defaultColumn: { minSize: 90, maxSize: 1000 },
    // state: { showColumnFilters: !lockOrder },
    initialState: {
      showColumnFilters: false,
      density: "compact",
      pagination: { pageIndex: 0, pageSize: PAGE_SIZE },
      // removes the empty trailing spacer column, so the table ends at the last column
      columnVisibility: { "mrt-row-spacer": false },
    },
    columnFilterDisplayMode: "subheader",
    layoutMode: "grid-no-grow",
    // table is exactly as wide as its columns; full width only in fullscreen
    muiTablePaperProps: ({ table }) => ({
      elevation: 0,
      sx: {
        width: table.getState().isFullScreen ? "100%" : tableWidth,
        maxWidth: "100%",
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        borderBottom: "none",
        borderRadius: "8px 8px 0 0",
      },
    }),
    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      onScroll: (e) => {
        const scrollLeft = (e.target as HTMLDivElement).scrollLeft;
        if (totalsInnerRef.current) {
          totalsInnerRef.current.style.transform = `translateX(-${scrollLeft}px)`;
        }
      },
      sx: {
        maxHeight: table.getState().isFullScreen
          ? "calc(100vh - 60px)"
          : TABLE_MAX_HEIGHT,
        overflowX: "auto",
        overflowY: "auto",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        backgroundColor: "#ffffff",
      },
    }),
    renderTopToolbar: ({ table }) => (
      <Box>
        <MRT_TopToolbar table={table} />
        {totals && (
          <TotalsBar table={table} totals={totals} innerRef={totalsInnerRef} />
        )}
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <TableColumnVisibilityMenu table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
    muiTableHeadRowProps: {
      sx: {
        background: "white",
        "& .MuiTableCell-root": {
          fontWeight: 600,
          fontSize: "0.75rem",
          color: "#2c3e50",
          borderRight: "1px solid #dcdcdc",
        },
        "& .MuiTableCell-root:last-of-type": { borderRight: "none" },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: "0.7rem",
        padding: "4px 8px",
        borderBottom: "1px solid #f1f1f1",
        borderRight: "1px solid #f1f1f1",
        "&:last-of-type": { borderRight: "none" },
        "&:hover": { backgroundColor: "#fafafa" },
      },
    },
    // HOF + Strategy: HOF rows stand out
    muiTableBodyRowProps: ({ row }) => ({
      sx: { ...combinedRowSx(row.original?.[ROW_TYPE_KEY]) },
    }),
    muiBottomToolbarProps: {
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "bottom-toolbar-wrapper__new",
    },
    muiTopToolbarProps: {
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "top-toolbar-wrapper__new",
    },
    muiPaginationProps: {
      shape: "rounded",
      size: "small",
      sx: {
        fontSize: "0.7rem",
        "& .MuiButtonBase-root": {
          fontSize: "0.7rem",
          minWidth: "28px",
          height: "28px",
        },
      },
    },
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "#e3f2fd",
        color: "#1976d2",
        fontSize: "0.75rem",
        borderRadius: 1,
      },
    },
  });

  return <MaterialReactTable table={table} />;
};

const InitialMarginSummary: React.FC = () => {
  const [businessDate, setBusinessDate] = useState<dayjs.Dayjs | null>(getLastBusinessDay);
  const [tillDate, setTillDate] = useState<dayjs.Dayjs | null>(getLastBusinessDay);
  // "Including" box: always sent as include_till_date=true/false; false leaves the selected date's trades out
  const [includeTillDate, setIncludeTillDate] = useState(true);
  // HOF mode: the hof jobs. HOF + Strategy mode: the hof_strategy jobs.
  const [jobs, setJobs] = useState<JobsState>(EMPTY_JOBS);
  // HOF + Strategy only: the second run's hof jobs (HOF row per book)
  const [hofJobs, setHofJobs] = useState<JobsState>(EMPTY_JOBS);
  // business_date the results were calculated for (from the run-im-all response)
  const [resultBusinessDate, setResultBusinessDate] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  // Rerun is offered once a run has finished; it repeats that run's dates with rerun=true
  const [hasFinishedRun, setHasFinishedRun] = useState(false);
  const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false);
  const lastRunParamsRef = useRef<RunParams | null>(null);
  // how long each book's calculation took (from clicking Run IM until its result arrived)
  const [timings, setTimings] = useState<Partial<Record<JobKey, JobTiming>>>({});
  // button that started the current run (for its spinner)
  const [runningMode, setRunningMode] = useState<MarginBy | null>(null);
  // mode whose results are on screen: "hof" or "hof_strategy" (strategies + HOF)
  const [resultMarginBy, setResultMarginBy] = useState<string | null>(null);
  const isCombined = resultMarginBy === "hof_strategy";

  // stops the polling loops once the page is left
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const patchJobs =
    (set: React.Dispatch<React.SetStateAction<JobsState>>) =>
    (key: JobKey, patch: JobPatch) =>
      set((prev) => ({
        ...prev,
        [key]: { ...(prev[key] as any), ...patch },
      }));
  const updateJob = patchJobs(setJobs);
  const updateHofJob = patchJobs(setHofJobs);

  // mode = which Run IM button was clicked; rerun repeats the last run
  // (same dates and mode) and forces a fresh calculation.
  // HOF + Strategy sends two requests: hof_strategy first, then hof with the
  // business_date the first one used, so both results are for the same ICE file
  // even if the day's files arrive in between.
  const handleRunAll = async (mode: MarginBy | null, rerun = false) => {
    if (rerun && !lastRunParamsRef.current) return;
    if (!rerun && !mode) return;

    const params = rerun
      ? (lastRunParamsRef.current as RunParams)
      : buildRunParams(businessDate, tillDate, includeTillDate, mode as MarginBy);
    if (!rerun) lastRunParamsRef.current = params;
    const combined = params.margin_by === "hof_strategy";

    setIsRunning(true);
    setRunningMode(params.margin_by);
    setJobs(EMPTY_JOBS);
    setHofJobs(EMPTY_JOBS);
    setTimings({});
    setResultMarginBy(null);
    setResultBusinessDate(null);
    if (!rerun) setHasFinishedRun(false);
    const runStartedAt = Date.now();

    try {
      const data = await startRunAll(params, rerun);
      if (!isMountedRef.current) return;
      const usedBusinessDate: string | undefined =
        data?.business_date ?? params.business_date;

      const hofData = combined
        ? await startRunAll(
            { ...params, margin_by: "hof", business_date: usedBusinessDate },
            rerun
          )
        : null;
      if (!isMountedRef.current) return;

      setResultMarginBy(params.margin_by);
      setResultBusinessDate(usedBusinessDate ?? hofData?.business_date ?? null);

      const started = buildStartedJobs(data);
      const startedHof = hofData ? buildStartedJobs(hofData) : EMPTY_JOBS;

      // Poll each job id once. If no book has more than one strategy both requests
      // can return the same job (identical IM), and that one poll then fills both.
      const listeners = new Map<string, ((patch: JobPatch) => void)[]>();
      const listen = (jobId: string | null, fn: (patch: JobPatch) => void) => {
        if (!jobId) return;
        listeners.set(jobId, [...(listeners.get(jobId) ?? []), fn]);
      };
      JOB_SECTIONS.forEach(({ key }) => {
        listen(started[key]?.jobId ?? null, (p) => updateJob(key, p));
        listen(startedHof[key]?.jobId ?? null, (p) => updateHofJob(key, p));
      });
      const pollsById = new Map<string, Promise<void>>();
      listeners.forEach((fns, jobId) => {
        pollsById.set(
          jobId,
          pollJob(
            jobId,
            (patch) => fns.forEach((fn) => fn(patch)),
            () => isMountedRef.current
          ).then((result) => {
            if (result === "done") setHasFinishedRun(true);
          })
        );
      });

      // a section's timer stops once all of its jobs (one, or two for HOF + Strategy) are in
      const startedTimings: Partial<Record<JobKey, JobTiming>> = {};
      JOB_SECTIONS.forEach(({ key }) => {
        const sectionPolls = [started[key]?.jobId, startedHof[key]?.jobId]
          .filter((id): id is string => !!id)
          .map((id) => pollsById.get(id)!);
        if (sectionPolls.length === 0) return;
        startedTimings[key] = { start: runStartedAt, end: null };
        Promise.all(sectionPolls).then(() => {
          if (!isMountedRef.current) return;
          setTimings((prev) =>
            prev[key]
              ? { ...prev, [key]: { ...prev[key]!, end: Date.now() } }
              : prev
          );
        });
      });

      setJobs(started);
      setHofJobs(startedHof);
      setTimings(startedTimings);

      const polls = Array.from(pollsById.values());
      if (polls.length === 0) {
        toast.warn("No positions found for organization or counterparty books.");
        return;
      }

      await Promise.all(polls);
    } catch (error: any) {
      console.error("Error running IM for all books:", error);
      toast.error(getRunErrorMessage(error));
    } finally {
      if (isMountedRef.current) {
        setIsRunning(false);
        setRunningMode(null);
      }
    }
  };

  // Total over both sections of the jobs that finished
  const sumDone = (state: JobsState) => {
    const done = JOB_SECTIONS.map(({ key }) => state[key]).filter(
      (j): j is NonNullable<typeof j> =>
        !!j && j.status === "done" && j.total !== null
    );
    return { count: done.length, total: done.reduce((s, j) => s + (j.total || 0), 0) };
  };
  const mainTotals = sumDone(jobs);
  const hofTotals = sumDone(hofJobs);
  // kept as before for the strip: jobs that finished / jobs that were started
  const doneJobs = { length: mainTotals.count + (isCombined ? hofTotals.count : 0) };
  const combinedTotal = mainTotals.total;
  const countStarted = (state: JobsState) =>
    JOB_SECTIONS.filter(({ key }) => state[key] && state[key]!.status !== "skipped")
      .length;
  const startedJobs = countStarted(jobs) + (isCombined ? countStarted(hofJobs) : 0);

  // result sections only appear after Run IM has been clicked
  const hasResults = JOB_SECTIONS.some(({ key }) => !!jobs[key]);

  // Rows shown per section: the job's rows, or for HOF + Strategy each book's
  // HOF row from the hof job, then its strategy rows. Each row also gets irm_sum.
  const sectionRows = useMemo(() => {
    const rows = {} as Record<JobKey, any[]>;
    JOB_SECTIONS.forEach(({ key }) => {
      const main = jobs[key]?.status === "done" ? jobs[key]!.margins : [];
      const built = isCombined
        ? buildCombinedRows(
            main,
            hofJobs[key]?.status === "done" ? hofJobs[key]!.margins : []
          )
        : main;
      rows[key] = withIrmSum(built);
    });
    return rows;
  }, [jobs, hofJobs, isCombined]);

  // Totals shown in the totals bar under the toolbar (Total IRM and IRM+Factor only).
  // Not shown in HOF + Strategy mode, where strategy and HOF rows are mixed.
  const sectionTotals = useMemo(() => {
    const totals = {} as Record<JobKey, Record<string, number> | null>;
    JOB_SECTIONS.forEach(({ key }) => {
      const rows = sectionRows[key];
      if (isCombined || rows.length === 0) {
        totals[key] = null;
        return;
      }
      const t: Record<string, number> = {};
      TOTAL_KEYS.forEach((k) => {
        t[k] =
          Math.round(
            rows.reduce((sum, r) => sum + (typeof r?.[k] === "number" ? r[k] : 0), 0) *
              100
          ) / 100;
      });
      totals[key] = t;
    });
    return totals;
  }, [sectionRows, isCombined]);

  const columnsBySection = useMemo(() => {
    const allRows: any[] = JOB_SECTIONS.flatMap(({ key }) => sectionRows[key]);
    const result = {} as Record<JobKey, MRT_ColumnDef<any>[]>;

    JOB_SECTIONS.forEach(({ key: sectionKey }) => {
      const totals = sectionTotals[sectionKey];

      result[sectionKey] = Object.entries(IM_RESULT_HEADERS)
        // HOF rows are one per book (strategy is null), so the Strategy column is left out
        .filter(([key]) => !(resultMarginBy === "hof" && key === "strategy"))
        .map(([key, header]) => {
          // longest rendered value in this column (incl. its total)
          const maxContent = allRows.reduce((max, r) => {
            const v = r?.[key];
            if (v === null || v === undefined) return max;
            const s =
              typeof v === "number"
                ? NON_MONEY_KEYS.includes(key)
                  ? v.toLocaleString(undefined, { maximumFractionDigits: 4 })
                  : formatMoney(v)
                : String(v);
            return Math.max(max, s.length);
          }, 0);
          const totalLen =
            totals && TOTAL_KEY_SET.has(key) ? formatMoney(totals[key] ?? 0).length : 0;

          const headerWidth = String(header).length * 8 + 70; // text + sort/filter icons
          const contentWidth = Math.max(maxContent, totalLen) * 8 + 56; // totals chip has extra padding
          const size = Math.max(120, headerWidth, contentWidth); // never narrower than its content

          return {
            accessorKey: key,
            header,
            size,
            Cell: ({ cell }: any) => {
              const value = cell.getValue();
              if (value === null || value === undefined || value === "") {
                return <span style={{ color: "#cbd5e1" }}>—</span>;
              }
              if (typeof value === "number") {
                // plain numbers (e.g. buffer factor): no money format, no colouring
                if (NON_MONEY_KEYS.includes(key)) {
                  return (
                    <span style={{ fontWeight: 600 }}>
                      {value.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                    </span>
                  );
                }
                return (
                  <span
                    style={{
                      color: value < 0 ? "#d32f2f" : "inherit",
                      fontWeight: 600,
                    }}
                  >
                    {formatMoney(value)}
                  </span>
                );
              }
              return <span title={String(value)}>{String(value)}</span>;
            },
          } as MRT_ColumnDef<any>;
        });
    });
    return result;
  }, [sectionRows, sectionTotals, resultMarginBy]);

  /* ---------- per-book result section ---------- */

  const renderJobSection = (key: JobKey, title: string) => {
    // HOF + Strategy: two jobs per section (strategies + HOF); otherwise one
    const parts = (
      isCombined
        ? [
            { label: "Strategies", job: jobs[key] },
            { label: "HOF", job: hofJobs[key] },
          ]
        : [{ label: "", job: jobs[key] }]
    ).filter((p): p is { label: string; job: NonNullable<typeof p.job> } => !!p.job);
    const statuses = parts.map((p) => p.job.status);
    const status =
      statuses.length === 0
        ? null
        : statuses.includes("failed")
        ? "failed"
        : statuses.every((s) => s === "skipped")
        ? "skipped"
        : statuses.every((s) => s === "done" || s === "skipped")
        ? "done"
        : statuses.includes("running")
        ? "running"
        : "queued";
    // section-level view of its job(s), so the markup below stays the same for both modes
    const job = status
      ? {
          status,
          positions: parts[0]?.job.positions ?? null,
          reused: parts.some((p) => p.job.reused),
          warnings: parts.flatMap((p) =>
            p.job.warnings.map((w) => (p.label ? `${p.label}: ${w}` : w))
          ),
          error: parts
            .filter((p) => p.job.status === "failed")
            .map((p) => (p.label ? `${p.label}: ${p.job.error}` : p.job.error))
            .join(" "),
          margins: sectionRows[key],
        }
      : null;
    // total in the heading: the job's total, or for HOF + Strategy only the HOF total
    const totals = parts
      .filter((p) => !isCombined || p.label === "HOF")
      .filter((p) => p.job.status === "done" && p.job.total !== null)
      .map((p) => ({
        label: p.label ? `${p.label} total` : "Total",
        value: p.job.total as number,
      }));
    const chip = job ? STATUS_CHIP[job.status] : null;
    const inProgress = job && ["queued", "running"].includes(job.status);
    const needsPaging = !!job && job.margins.length > PAGE_SIZE;

    return (
      <Box>
        {/* Section heading: accent bar, title, status, positions, total */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            pb: 0.75,
            mb: 1,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Box sx={{ width: 4, height: 16, borderRadius: 2, bgcolor: BRAND }} />
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#0f172a" }}>
            {title}
          </Typography>
          {job && (
            <Chip
              size="small"
              variant="outlined"
              label={chip?.label || job.status}
              color={chip?.color || "default"}
              sx={{ fontSize: "0.62rem", height: 17 }}
            />
          )}
          {inProgress && <CircularProgress size={12} thickness={5} />}
          {timings[key] && <ElapsedTimer timing={timings[key]!} />}
          {job?.positions != null && (
            <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>
              {job.positions} positions
            </Typography>
          )}
          {totals.map((t) => (
            <React.Fragment key={t.label}>
              <Box sx={{ width: "1px", height: 14, bgcolor: BORDER }} />
              <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>
                {t.label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  color: moneyColor(t.value),
                }}
              >
                {formatMoney(t.value)}
              </Typography>
            </React.Fragment>
          ))}
          {job?.status === "done" && job.reused && (
            <Typography
              component="span"
              sx={{
                fontSize: "0.6rem",
                fontWeight: 600,
                color: "#b26a00",
                backgroundColor: "#fff4e5",
                borderRadius: "8px",
                px: 0.75,
              }}
            >
              Reused from earlier run
            </Typography>
          )}
        </Box>

        {!job && (
          <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "0.75rem" }}>
            Not run yet.
          </Typography>
        )}

        {inProgress && (
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
            Calculating… this usually takes a couple of minutes.
          </Typography>
        )}

        {job?.status === "skipped" && (
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
            No positions found, nothing to calculate.
          </Typography>
        )}

        {job?.status === "failed" && (
          <Typography variant="body2" sx={{ color: "#b91c1c", fontSize: "0.75rem" }}>
            {job.error}
          </Typography>
        )}

        {job?.status === "done" && job.warnings.length > 0 && (
          <Box
            sx={{
              mb: 0.75,
              px: 1,
              py: 0.5,
              borderRadius: "6px",
              bgcolor: "#f8fafc",
              border: `1px solid ${BORDER}`,
            }}
          >
            {job.warnings.map((w, i) => (
              <Typography
                key={i}
                variant="caption"
                sx={{ display: "block", color: "#475569", lineHeight: 1.4 }}
              >
                {w}
              </Typography>
            ))}
          </Box>
        )}

        {/* shown when done, and also next to a failure of the other mode's job */}
        {(job?.status === "done" || (job?.status === "failed" && job.margins.length > 0)) &&
          (job.margins.length > 0 ? (
            <div className="table-ad__wrapperCard">
              <ResultTable
                columns={columnsBySection[key]}
                data={job.margins}
                totals={sectionTotals[key]}
                lockOrder={isCombined}
                needsPaging={needsPaging}
              />
            </div>
          ) : (
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
              Finished, but no margin rows were returned.
            </Typography>
          ))}
      </Box>
    );
  };

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              {/* ===== Accordion form ===== */}
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header
                    className="accordion_button"
                    style={{
                      fontSize: "0.8rem",
                      padding: "6px 12px",
                      borderRadius: "4px",
                      fontWeight: 600,
                    }}
                  >
                    Initial Margin Summary
                  </Accordion.Header>
                  <Accordion.Body>
                    {/* Inputs + action */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="IM File Date"
                          format={BUSINESS_DATE_FORMAT}
                          value={businessDate}
                          onChange={(newValue) => setBusinessDate(newValue)}
                          disabled={isRunning}
                          slotProps={{
                            textField: {
                              size: "small",
                              InputLabelProps: { shrink: true },
                              sx: datePickerSx,
                            },
                            field: { clearable: true },
                          }}
                        />
                        <DatePicker
                          label="Trades till"
                          format={TILL_DATE_FORMAT}
                          value={tillDate}
                          onChange={(newValue) => setTillDate(newValue)}
                          disabled={isRunning}
                          slotProps={{
                            textField: {
                              size: "small",
                              InputLabelProps: { shrink: true },
                              sx: datePickerSx,
                            },
                            field: { clearable: true },
                          }}
                        />
                      </LocalizationProvider>
                      <Tooltip
                        arrow
                        title={
                          includeTillDate
                            ? "ON: trades dated on the selected date are included in the calculation."
                            : "OFF: trades on the selected date are left out; only trades up to the previous business day count."
                        }
                      >
                        <FormControlLabel
                          disabled={isRunning}
                          sx={{
                            m: 0,
                            pr: 1.25,
                            height: 34,
                            borderRadius: "8px",
                            border: `1px solid ${
                              includeTillDate ? BRAND : "rgba(150,160,180,0.4)"
                            }`,
                            background: includeTillDate
                              ? "#e6f6f9"
                              : "linear-gradient(160deg, #edf4ff 0%, #f5f8ff 100%)",
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              color: includeTillDate ? "#007b8a" : "#3b4a66",
                              whiteSpace: "nowrap",
                            },
                          }}
                          control={
                            <Checkbox
                              size="small"
                              checked={includeTillDate}
                              onChange={(e) => setIncludeTillDate(e.target.checked)}
                              sx={{
                                color: "#94a3b8",
                                "&.Mui-checked": { color: BRAND },
                              }}
                            />
                          }
                          label="Include selected date's trades"
                        />
                      </Tooltip>

                      {MARGIN_BY_MODES.map((mode, i) => (
                        <Tooltip key={mode.value} arrow title={mode.hint}>
                          <span>
                            <Button
                              variant={i === 0 ? "contained" : "outlined"}
                              disableElevation
                              onClick={() => handleRunAll(mode.value)}
                              disabled={isRunning}
                              startIcon={
                                runningMode === mode.value ? (
                                  <CircularProgress
                                    size={14}
                                    thickness={5}
                                    sx={{ color: i === 0 ? "#fff" : BRAND }}
                                  />
                                ) : (
                                  <CalculateIcon fontSize="small" />
                                )
                              }
                              sx={{
                                height: 36,
                                px: 2.5,
                                textTransform: "none",
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                borderRadius: "8px",
                                whiteSpace: "nowrap",
                                ...(i === 0
                                  ? {
                                      background: `linear-gradient(135deg, ${BRAND} 0%, #007b8a 100%)`,
                                      boxShadow: "0 2px 8px rgba(0,157,181,0.35)",
                                      "&:hover": {
                                        background:
                                          "linear-gradient(135deg, #008699 0%, #006b78 100%)",
                                      },
                                    }
                                  : {
                                      color: "#007b8a",
                                      borderColor: BRAND,
                                      backgroundColor: "#fff",
                                      "&:hover": {
                                        borderColor: "#007b8a",
                                        backgroundColor: "#e6f6f9",
                                      },
                                    }),
                              }}
                            >
                              {runningMode === mode.value
                                ? "Calculating…"
                                : `Run IM (${mode.label})`}
                            </Button>
                          </span>
                        </Tooltip>
                      ))}
                    </Box>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              {/* ===== TOTAL summary: compact, left-aligned strip ===== */}
              {startedJobs > 0 && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    mt: 2,
                    mb: 2.5,
                    px: 2,
                    py: 0.75,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg,#ffffff 0%,#f4fbfc 100%)",
                    border: `1px solid ${BORDER}`,
                    borderLeft: `4px solid ${BRAND}`,
                    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
                  }}
                >
                  {/* one total; for HOF + Strategy only the HOF total */}
                  {(isCombined
                    ? [{ label: "HOF total", ...hofTotals }]
                    : [{ label: "Total Initial Margin", count: mainTotals.count, total: combinedTotal }]
                  ).map((t) => (
                    <Box
                      key={t.label}
                      sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}
                    >
                      <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#64748b" }}>
                        {t.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color:
                            t.count === 0
                              ? "text.disabled"
                              : t.total < 0
                                ? "#d32f2f"
                                : "#2e7d32",
                        }}
                      >
                        {t.count > 0 ? formatMoney(t.total) : "—"}
                      </Typography>
                    </Box>
                  ))}

                  {resultBusinessDate && (
                    <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>
                      ICE File Date:{" "}
                      <b style={{ color: "#1e293b" }}>
                        {formatBusinessDate(resultBusinessDate)}
                      </b>
                    </Typography>
                  )}

                  {resultMarginBy && (
                    <Chip
                      size="small"
                      label={marginByLabel(resultMarginBy)}
                      sx={{
                        fontSize: "0.62rem",
                        height: 18,
                        fontWeight: 600,
                        color: "#007b8a",
                        backgroundColor: "#e6f6f9",
                      }}
                    />
                  )}

                  <Box sx={{ width: "1px", height: 16, bgcolor: BORDER }} />

                  <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>
                    Calculations:{" "}
                    <b style={{ color: "#1e293b" }}>
                      {doneJobs.length}/{startedJobs}
                    </b>
                  </Typography>

                  {hasFinishedRun && (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={isRunning}
                      onClick={() => setRerunConfirmOpen(true)}
                      startIcon={
                        isRunning ? (
                          <CircularProgress size={10} thickness={5} />
                        ) : (
                          <RefreshIcon sx={{ fontSize: "0.8rem !important" }} />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        py: 0,
                        px: 1,
                        minHeight: 22,
                        borderRadius: "4px",
                      }}
                    >
                      {isRunning ? "Recalculating…" : "Rerun"}
                    </Button>
                  )}
                </Box>
              )}

              {/* ===== RESULT SECTIONS: only after Run IM; stacked full width, each as a card ===== */}
              {hasResults && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                    mt: startedJobs > 0 ? 0 : 2,
                  }}
                >
                  {JOB_SECTIONS.map(({ key, title }) => (
                    <Box
                      key={key}
                      sx={{
                        background: "#fff",
                        border: `1px solid ${BORDER}`,
                        borderRadius: "12px",
                        p: 1.5,
                        boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
                      }}
                    >
                      {renderJobSection(key, title)}
                    </Box>
                  ))}
                </Box>
              )}
            </div>
          </div>
        </Row>
      </Container>

      {/* ===== Dialogs: outside Container, directly inside page-body ===== */}
      <Dialog
        open={rerunConfirmOpen}
        onClose={() => setRerunConfirmOpen(false)}
        PaperProps={{ sx: { width: 360, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700 }}>
          Rerun Initial Margin
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.8rem" }}>
            This will recalculate margin and can take a few minutes. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            onClick={() => setRerunConfirmOpen(false)}
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setRerunConfirmOpen(false);
              handleRunAll(null, true);
            }}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND,
              "&:hover": { backgroundColor: "#008699" },
            }}
          >
            Rerun
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InitialMarginSummary;